import { focusOnPointAnimated } from "../../utils/viewerHelpers.js";
import { getCurrentCampusCode } from "../../utils/campusHelpers.js";
import { resetIndoorLayer, buildFloorToBuildingCache } from "./floorManager.js";
import {
  searchPinActions,
  gpsActions,
  mapActions,
} from "../reducers/mapPanZoomReducer.js";
import campusData from "../../../json/campus_data.json";
import searchData from "../../../json/search_data.json";

/**
 * キャンパス変更ハンドラ
 * @param {object} viewerRef マップビューアref
 * @param {function} dispatch 統合dispatch
 * @param {function} updateSearchParamsFromSelection URLクエリ更新関数
 * @param {number} campusValue 切り替え先キャンパスID
 */
export function handleCampusChange(
  viewerRef,
  dispatch,
  updateSearchParamsFromSelection,
  campusValue,
) {
  const viewer = viewerRef.current;

  updateSearchParamsFromSelection({
    target_id: null,
    campus: campusValue,
  });
  viewer?.reset?.();

  dispatch(mapActions.changeCampus(campusValue));
  dispatch(searchPinActions.clear());
  dispatch(gpsActions.resetMarker());
}

/**
 * フロア・部屋配列を生成
 * @param {string[]} currentBuildingArray 建物配列
 * @param {Record<string, object>} currentMapJson マップJSON
 * @returns {{ currentFloorArray: Array, currentRoomArray: Array }}
 */
function generateFloorRoomArrayInternal(currentBuildingArray, currentMapJson) {
  const buildings = currentBuildingArray
    .map((value) => currentMapJson[value])
    .filter(Boolean);

  const currentFloorArray = buildings.flatMap((value) =>
    Array.isArray(value.floors) ? value.floors : [],
  );

  const currentRoomArray = currentFloorArray.flatMap((value) =>
    Array.isArray(value.rooms) ? value.rooms : [],
  );

  return { currentFloorArray, currentRoomArray };
}

/**
 * 検索配列を生成
 * @param {number} currentCampus 現在のキャンパス
 * @returns {Array} 検索配列
 */
function generateSearchArrayInternal(currentCampus) {
  const campusSearchData = searchData[currentCampus - 1];
  return campusSearchData ? campusSearchData.items : [];
}

/**
 * SVGを読み込んだ後の初期化処理
 * @param {object} viewerRef マップビューアref
 * @param {object} svgRootRef SVGルートref
 * @param {object} floorStoreRef フロアストアref
 * @param {function} dispatch 統合dispatch
 * @param {number} currentCampus 現在のキャンパス
 * @param {Array} mapJson マップJSONデータ
 */
export function initializeMap(
  viewerRef,
  svgRootRef,
  floorStoreRef,
  dispatch,
  currentCampus,
  mapJson,
) {
  const viewer = viewerRef.current;
  const svgRoot = svgRootRef.current;
  const floorStore = floorStoreRef.current;

  const campusIdx = currentCampus - 1;
  const currentMapJson = mapJson[campusIdx] || {};
  const currentBuildingArray = Object.keys(currentMapJson);

  // フロア・部屋配列を生成
  const { currentFloorArray, currentRoomArray } =
    generateFloorRoomArrayInternal(currentBuildingArray, currentMapJson);

  // 検索配列を生成
  const currentSearchArray = generateSearchArrayInternal(currentCampus);

  // キャンパスコードを更新
  const currentCampusCode = getCurrentCampusCode(currentCampus, campusData);

  // dispatch で一括更新
  dispatch(
    mapActions.initialize({
      currentMapJson,
      currentBuildingArray,
      currentFloorArray,
      currentRoomArray,
      currentSearchArray,
      currentCampusCode,
    }),
  );

  // フロアキャッシュをビルド
  buildFloorToBuildingCache(floorStore, currentBuildingArray, currentMapJson);

  // viewer リセットと indoor layer リセット
  viewer?.reset?.();
  resetIndoorLayer(floorStore, svgRoot, currentCampus);

  // 初期フォーカス位置へ移動
  const campusMap = campusData.campusList[currentCampus - 1]?.map ?? {};
  const focusX = campusMap?.initialFocus?.x || null;
  const focusY = campusMap?.initialFocus?.y || null;
  const zoomLevel = campusMap?.initialZoom || null;

  if (focusX != null && focusY != null && zoomLevel != null) {
    focusOnPointAnimated(viewer, svgRoot, focusX, focusY, zoomLevel, 1000);
  }
}

/**
 * 検索ピンをクリア
 * @param {function} dispatch 統合dispatch
 */
export function clearSearchPin(dispatch) {
  dispatch(searchPinActions.clear());
}
